import { useEffect } from "react";
import { useSearchParams } from "react-router-dom";

const REFERRAL_KEY = "elara_ref";
const COMMISSION_KEY = "elara_commission";
const COOKIE_MAX_AGE = 2592000; // 30 days in seconds

// UTM keys (session-scoped — intentionally kept in sessionStorage)
const UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"] as const;
const UTM_STORAGE_PREFIX = "elara_";
const LANDING_PAGE_KEY = "elara_landing_page";
const REFERRER_KEY = "elara_referrer";

/* ---------- Cookie helpers ---------- */

function setCookie(name: string, value: string, maxAge: number) {
  document.cookie = `${name}=${encodeURIComponent(value)}; path=/; max-age=${maxAge}; SameSite=Lax; Secure`;
}

function getCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : null;
}

function deleteCookie(name: string) {
  document.cookie = `${name}=; path=/; max-age=0; SameSite=Lax; Secure`;
}

/* ---------- Hooks ---------- */

/**
 * Captures `?ref=SALESPERSON_ID` (and optional `&commission=RATE`)
 * plus all UTM parameters from the URL.
 *
 * Referral code is stored in a **30-day first-party cookie** so it
 * survives browser restarts. UTM params stay in sessionStorage
 * (session-scoped by nature). Also captures landing page and referrer.
 */
export const useReferralCapture = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    let dirty = false;

    // Capture referral → cookie (first touch wins within 30 days)
    const ref = searchParams.get("ref");
    if (ref) {
      // Only overwrite if there's no existing cookie (first-touch attribution)
      if (!getCookie(REFERRAL_KEY)) {
        setCookie(REFERRAL_KEY, ref, COOKIE_MAX_AGE);
      }
      const commission = searchParams.get("commission");
      if (commission && !getCookie(COMMISSION_KEY)) {
        setCookie(COMMISSION_KEY, commission, COOKIE_MAX_AGE);
      }
      searchParams.delete("ref");
      searchParams.delete("commission");
      dirty = true;
    }

    // Capture UTM parameters (only set once per session — first touch wins)
    for (const key of UTM_KEYS) {
      const val = searchParams.get(key);
      if (val) {
        const storageKey = `${UTM_STORAGE_PREFIX}${key}`;
        if (!sessionStorage.getItem(storageKey)) {
          sessionStorage.setItem(storageKey, val);
        }
        searchParams.delete(key);
        dirty = true;
      }
    }

    // Capture landing page and referrer (once per session)
    if (!sessionStorage.getItem(LANDING_PAGE_KEY)) {
      sessionStorage.setItem(LANDING_PAGE_KEY, window.location.href);
    }
    if (!sessionStorage.getItem(REFERRER_KEY) && document.referrer) {
      sessionStorage.setItem(REFERRER_KEY, document.referrer);
    }

    if (dirty) {
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);
};

/**
 * Returns the stored referral data (salesperson ID + commission rate).
 * Reads from the 30-day cookie.
 */
export const getReferralData = () => {
  const commissionStr = getCookie(COMMISSION_KEY);
  return {
    salespersonId: getCookie(REFERRAL_KEY),
    commissionRate: commissionStr ? parseFloat(commissionStr) : null,
  };
};

/**
 * Returns captured UTM attribution data (session-scoped).
 */
export const getUtmData = () => {
  return {
    utm_source: sessionStorage.getItem(`${UTM_STORAGE_PREFIX}utm_source`) || null,
    utm_medium: sessionStorage.getItem(`${UTM_STORAGE_PREFIX}utm_medium`) || null,
    utm_campaign: sessionStorage.getItem(`${UTM_STORAGE_PREFIX}utm_campaign`) || null,
    utm_content: sessionStorage.getItem(`${UTM_STORAGE_PREFIX}utm_content`) || null,
    utm_term: sessionStorage.getItem(`${UTM_STORAGE_PREFIX}utm_term`) || null,
    landing_page: sessionStorage.getItem(LANDING_PAGE_KEY) || null,
    referrer: sessionStorage.getItem(REFERRER_KEY) || null,
  };
};

export const clearReferral = () => {
  deleteCookie(REFERRAL_KEY);
  deleteCookie(COMMISSION_KEY);
};

export const clearUtmData = () => {
  for (const key of UTM_KEYS) {
    sessionStorage.removeItem(`${UTM_STORAGE_PREFIX}${key}`);
  }
  sessionStorage.removeItem(LANDING_PAGE_KEY);
  sessionStorage.removeItem(REFERRER_KEY);
};
